import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import {
  HiChevronDown,
  HiInformationCircle,
  HiOutlinePlus,
  HiOutlineX,
} from "react-icons/hi";

import StudentAvatar from "../TaggedStudentPanel/StudentAvatar";
import type { Student } from "types/Student";
import type { TagStudent } from "types/TagStudent";

interface TagStudentManagementPanelProps {
  taggedStudents: TagStudent[];
  availableStudents: Student[];
  isLoadingTags: boolean;
  removingTagId: number | null;
  armedStudentId: number | null;
  onArmStudent: (studentId: number) => void;
  onCancelArmedStudent: () => void;
  onRemoveTag: (tagId: number) => void;
}

/**
 * The pre-8-1 tag management UI, restored to sit alongside the on-photo
 * click-to-tag flow (2026-08 client request: keep both entry points).
 * Adding a student here arms them and waits for the next photo click to
 * place the marker; the on-photo popover flow keeps working independently.
 */
const TagStudentManagementPanel: React.FC<TagStudentManagementPanelProps> = ({
  taggedStudents,
  availableStudents,
  isLoadingTags,
  removingTagId,
  armedStudentId,
  onArmStudent,
  onCancelArmedStudent,
  onRemoveTag,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const infoIconRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (showInfoTooltip && infoIconRef.current) {
      const rect = infoIconRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  }, [showInfoTooltip]);

  const normalizedSearch = search.trim().toLowerCase();

  const matches = (student: Student) => {
    if (!normalizedSearch) return true;
    const tokens = [
      `${student.last_name} ${student.first_name}`,
      `${student.first_name} ${student.last_name}`,
      `${student.last_name_kata ?? ""} ${student.first_name_kata ?? ""}`,
      `${student.id}`,
    ];
    return tokens.some((token) =>
      token
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim()
        .includes(normalizedSearch),
    );
  };

  const filteredTagged = useMemo(
    () =>
      taggedStudents.filter((tag) => {
        if (!normalizedSearch) return true;
        if (!tag.student) return false;
        const tokens = [
          `${tag.student.last_name} ${tag.student.first_name}`,
          `${tag.student.first_name} ${tag.student.last_name}`,
          `${tag.student.last_name_kata ?? ""} ${tag.student.first_name_kata ?? ""}`,
        ];
        return tokens.some((token) =>
          token
            .toLowerCase()
            .replace(/\s+/g, " ")
            .trim()
            .includes(normalizedSearch),
        );
      }),
    [taggedStudents, normalizedSearch],
  );

  const filteredAvailable = useMemo(
    () => availableStudents.filter(matches),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availableStudents, normalizedSearch],
  );

  return (
    <div className="w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
        aria-label={t("_tagged_student_panel._toggle_panel_aria_label", {
          defaultValue: "Toggle the student tag management panel",
        })}
        className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-200"
      >
        <div className="flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold text-gray-900">
              {t("_tagged_student_panel._title", { defaultValue: "園児タグ" })}
            </p>
            <span
              ref={infoIconRef}
              className="relative inline-flex shrink-0 items-center"
              onClick={(event) => event.stopPropagation()}
              onMouseEnter={() => setShowInfoTooltip(true)}
              onMouseLeave={() => setShowInfoTooltip(false)}
            >
              <HiInformationCircle
                className="h-4 w-4 text-gray-600"
                aria-label={t(
                  "_tagged_student_panel._info_tooltip_aria_label",
                  { defaultValue: "About student tags" },
                )}
              />
            </span>
            {showInfoTooltip &&
              // Portaled to escape the panel's overflow-hidden, same as
              // ClassPictureButton's tooltip (see its portal-safety note).
              ReactDOM.createPortal(
                <div
                  className="pointer-events-none fixed z-[9999] w-60 whitespace-pre-line rounded bg-gray-800 px-2 py-1.5 text-sm font-normal leading-snug text-white shadow"
                  style={{
                    top: tooltipPosition.top,
                    left: tooltipPosition.left,
                  }}
                >
                  {t("_tagged_student_panel._info_tooltip", {
                    defaultValue:
                      "You can also add a student tag by clicking directly on the photo.\n*Student tags are not shown in the parent app.",
                  })}
                </div>,
                document.body,
              )}
          </div>
          <p className="text-xs text-gray-500">
            {t("_tagged_student_panel._description", {
              defaultValue: "この写真にタグ付けされた園児を管理します。",
            })}
          </p>
        </div>
        <HiChevronDown
          aria-hidden="true"
          className={`h-5 w-5 shrink-0 text-gray-500 transition-transform duration-200 ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {isExpanded && (
        <div className="space-y-3 border-t border-gray-100 px-4 py-3">
          {armedStudentId !== null && (
            <div className="flex items-center justify-between gap-2 rounded-md bg-emerald-50 px-2 py-1.5 text-xs text-emerald-800">
              <span>
                {t("_tagged_student_panel._tap_photo_to_place_tag", {
                  defaultValue:
                    "写真をタップまたはクリックしてタグを配置します。",
                })}
              </span>
              <button
                type="button"
                onClick={onCancelArmedStudent}
                className="shrink-0 font-medium underline"
              >
                {t("_tagged_student_panel._cancel", {
                  defaultValue: "キャンセル",
                })}
              </button>
            </div>
          )}

          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("_tagged_student_panel._search_placeholder", {
              defaultValue: "園児を検索…",
            })}
            className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
          />

          <div>
            <h4 className="mb-1 text-xs font-semibold text-gray-700">
              {t("_tagged_student_panel._tagged_students", {
                defaultValue: "タグ付けされた園児",
              })}
            </h4>
            {isLoadingTags ? (
              <p className="px-1 py-2 text-xs text-gray-500">…</p>
            ) : filteredTagged.length === 0 ? (
              <p className="px-1 py-2 text-xs text-gray-500">
                {t("_tagged_student_panel._no_students_are_tagged", {
                  defaultValue: "まだタグ付けされた園児はいません。",
                })}
              </p>
            ) : (
              <ul className="max-h-48 space-y-1.5 overflow-y-auto">
                {filteredTagged.map((tag) => {
                  const displayName = tag.student
                    ? `${tag.student.last_name} ${tag.student.first_name}`.trim()
                    : t("_tagged_student_panel._unknown_student", {
                        defaultValue: "不明な園児",
                      });
                  const kanaName = tag.student
                    ? `${tag.student.last_name_kata ?? ""} ${
                        tag.student.first_name_kata ?? ""
                      }`.trim()
                    : "";
                  const isRemoving = removingTagId === tag.id;
                  return (
                    <li
                      key={tag.id}
                      className={`flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm ${
                        isRemoving ? "opacity-70" : ""
                      }`}
                    >
                      <div className="flex items-center gap-3 overflow-hidden">
                        <StudentAvatar student={tag.student} size="md" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {displayName}
                          </p>
                          {kanaName && (
                            <p className="truncate text-xs text-gray-500">
                              {kanaName}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={isRemoving}
                        onClick={() => onRemoveTag(tag.id)}
                        aria-label={t(
                          "_tagged_student_panel._remove_student_aria_label",
                          { studentName: displayName },
                        )}
                        className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-rose-200 px-3 py-1 text-xs font-medium text-rose-600 transition-all duration-150 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-200 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isRemoving ? (
                          <span className="inline-block h-4 w-4 animate-spin rounded-full border border-rose-300 border-t-transparent" />
                        ) : (
                          <HiOutlineX className="h-4 w-4" />
                        )}
                        {t("_tagged_student_panel._remove", {
                          defaultValue: "削除",
                        })}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div>
            <h4 className="mb-1 text-xs font-semibold text-gray-700">
              {t("_tagged_student_panel._available_students", {
                defaultValue: "利用可能な園児",
              })}
            </h4>
            {filteredAvailable.length === 0 ? (
              <p className="px-1 py-2 text-xs text-gray-500">
                {t("_tagged_student_panel._all_eligible_students_are_tagged", {
                  defaultValue:
                    "対象となるすべての園児はすでにタグ付けされています。",
                })}
              </p>
            ) : (
              <ul className="max-h-48 space-y-1.5 overflow-y-auto">
                {filteredAvailable.map((student) => {
                  const displayName =
                    `${student.last_name} ${student.first_name}`.trim();
                  const kanaName = `${student.last_name_kata ?? ""} ${
                    student.first_name_kata ?? ""
                  }`.trim();
                  const isArmed = armedStudentId === student.id;
                  return (
                    <li
                      key={student.id}
                      className={`flex items-center justify-between gap-3 rounded-lg border bg-white px-3 py-2 shadow-sm ${
                        isArmed
                          ? "border-emerald-300 bg-emerald-50/60 shadow-md"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-center gap-3 overflow-hidden">
                        <StudentAvatar student={student} size="md" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {displayName}
                          </p>
                          {kanaName && (
                            <p className="truncate text-xs text-gray-500">
                              {kanaName}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onArmStudent(student.id)}
                        disabled={armedStudentId !== null}
                        aria-label={t("_tagged_student_panel._tag_student", {
                          studentName: displayName,
                          defaultValue: "Tag {{studentName}}",
                        })}
                        className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-600 transition-all duration-150 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <HiOutlinePlus className="h-4 w-4" />
                        {t("_tagged_student_panel._tag", {
                          defaultValue: "タグ付け",
                        })}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagStudentManagementPanel;
